import classNames from 'classnames';
import { ComponentProps, FC, useEffect, useRef } from 'react';
import { ReactSVG } from 'react-svg';
import Tooltip from 'react-tooltip-lite';

import { useTranslation } from 'utils/i18n';
import {
  DESKTOP_TOOLTIP_DISTANCE,
  MOBILE_TOOLTIP_DISTANCE,
} from '../../../constants/breakpoints';

import useIsMobile from '../../../hooks/useIsMobile';
import SelectList from '../selectList';

interface ClientService {
  clinical: boolean;
  name: string;
  id: number;
}

interface Props extends ComponentProps<typeof SelectList> {
  services: ClientService[];
  clinical?: boolean;
  showError?: boolean;
  autoSelect?: boolean;
  toolTip?: string;
  // Receives the service id: the raw numeric id when auto-selecting the only
  // option, but the DOM-stringified form when the user picks from the list.
  onChange?: (value: string | number) => void;
}

const noOp = () => {};

const ServiceSelect: FC<Props> = ({
  services = [],
  clinical = false,
  loading = false,
  showError = true,
  autoSelect = false,
  onChange: setValue = noOp,
  toolTip = '',
  ...props
}) => {
  const { t } = useTranslation('common');
  const options = services
    .filter(service => !(clinical && !service.clinical))
    .map(service => ({
      label: service.name,
      value: service.id,
    }));

  const [{ label: singleLabel, value: singleValue } = {}] =
    options.length === 1 ? options : [];

  const setValueRef = useRef(setValue);

  const isMobileView = useIsMobile();

  useEffect(() => {
    setValueRef.current = setValue;
  });

  useEffect(() => {
    if (autoSelect && singleValue) setValueRef.current(singleValue);
  }, [autoSelect, singleValue]);

  const { meta = {} } = props as { meta?: { error?: string } };

  const classes = classNames('field', 'field--info-only', {
    'field--has-error': !!meta.error,
  });

  return singleLabel ? (
    <div className={classes} data-testid="serviceTypeSingle">
      <div className="field__title">{t('shared.service')}</div>
      <div className="field__desc">{singleLabel}</div>
      {toolTip && (
        <Tooltip
          content={toolTip}
          className="tooltip"
          distance={
            isMobileView ? MOBILE_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
          }
        >
          <ReactSVG
            src="/images/icons/blue-question-mark.svg"
            width={17}
            height={17}
          />
        </Tooltip>
      )}
    </div>
  ) : (
    <SelectList
      label={t('shared.service')}
      placeholder={t('shared.service')}
      name="service"
      testid="serviceTypeMultiple"
      options={options}
      onChange={setValue}
      {...(props as Omit<typeof props, 'label' | 'name'>)}
      loading={loading || !services.length}
      error={!!meta.error}
      errorMsg={showError ? meta.error : undefined}
    />
  );
};

export default ServiceSelect;
